<template>
  <div class="manual-page">
    <!-- 付款人姓名弹框 -->
    <div class="payer-dialog-overlay" v-if="showPayerDialog">
      <div class="payer-dialog">
        <div class="dialog-header">
          <span class="dialog-icon">👤</span>
          <h3 class="dialog-title">请输入付款人姓名</h3>
        </div>
        <div class="dialog-content">
          <p class="dialog-desc">为了确认您的支付身份，请输入银行卡付款人姓名</p>
          <div class="input-wrapper">
            <input 
              type="text" 
              v-model="payerName"
              class="payer-input"
              placeholder="请输入付款人姓名"
              maxlength="20"
              @keyup.enter="submitPayerName"
              ref="payerInput"
            />
          </div>
          <p class="input-tip">温馨提示：请确保输入的姓名与银行卡开户名一致</p>
        </div>
        <div class="dialog-footer">
          <button class="submit-btn" @click="submitPayerName" :disabled="!payerName.trim()">
            <span class="btn-text">提交</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 顶部标题区 -->
    <header class="page-header">
      <div class="header-icon-wrapper">
        <div class="header-icon">💳</div>
      </div>
      <h1 class="page-title">银行卡转账支付</h1>
      <p class="page-subtitle">请按照以下信息完成转账</p>
      <!-- 倒计时显示 -->
      <div class="header-countdown">
        <span class="countdown-icon">⏰</span>
        <span class="countdown-text">{{ formattedCountdown }}</span>
      </div>
    </header>

    <div class="content-wrapper">
      <!-- 重要警告提示 -->
      <section class="warning-section">
        <div class="warning-card">
          <div class="warning-header">
            <span class="warning-icon">🚫</span>
            <span class="warning-title">重要提示</span>
          </div>
          <div class="warning-content">
            <p class="warning-main">请勿手动刷新或关闭页面</p>
            <p class="warning-sub">平台确认收款后，页面会自动跳转至完成页面</p>
          </div>
        </div>
      </section>

      <!-- 金额卡片 -->
      <section class="amount-section" v-if="payOrderInfo.amount">
        <div class="amount-card">
          <div class="amount-header">
            <span class="amount-icon">💰</span>
            <span class="amount-label">应付金额</span>
          </div>
          <div class="amount-value">￥{{ formatAmount(payOrderInfo.amount) }}</div>
          <div class="amount-order">订单号：{{ payOrderInfo.mchOrderNo || '-' }}</div>
        </div>
      </section>

      <!-- 收款信息卡片 -->
      <section class="account-section">
        <div class="section-header">
          <span class="section-icon">🏦</span>
          <h2 class="section-title">收款账户信息</h2>
        </div>
        <div class="account-info">
          <div class="info-item" v-if="payOrderInfo.receiveAccountName">
            <div class="info-label">
              <span class="label-icon">👤</span>
              <span>收款人</span>
            </div>
            <div class="info-value-wrapper">
              <span class="info-value">{{ payOrderInfo.receiveAccountName }}</span>
              <button class="copy-btn" @click="copyToClipboard(payOrderInfo.receiveAccountName, '姓名')">
                <span class="copy-icon">📋</span>
                <span class="copy-text">复制</span>
              </button>
            </div>
          </div>

          <div class="info-item" v-if="payOrderInfo.receiveBankName">
            <div class="info-label">
              <span class="label-icon">🏛️</span>
              <span>开户银行</span>
            </div>
            <div class="info-value-wrapper">
              <span class="info-value">{{ payOrderInfo.receiveBankName }}</span>
              <button class="copy-btn" @click="copyToClipboard(payOrderInfo.receiveBankName, '银行')">
                <span class="copy-icon">📋</span>
                <span class="copy-text">复制</span>
              </button>
            </div>
          </div>

          <div class="info-item card-item" v-if="payOrderInfo.receiveAccountNo">
            <div class="info-label">
              <span class="label-icon">💳</span>
              <span>银行卡号</span>
            </div>
            <div class="info-value-wrapper">
              <span class="info-value card-number">{{ formatCardNumber(payOrderInfo.receiveAccountNo) }}</span>
              <button class="copy-btn primary" @click="copyToClipboard(payOrderInfo.receiveAccountNo, '卡号')">
                <span class="copy-icon">📋</span>
                <span class="copy-text">复制</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- 操作步骤引导 -->
      <section class="steps-section">
        <div class="section-header">
          <span class="section-icon">📋</span>
          <h2 class="section-title">转账步骤</h2>
        </div>
        <div class="steps-list">
          <div class="step-item">
            <div class="step-number">1</div>
            <div class="step-content">
              <div class="step-title">复制收款信息</div>
              <div class="step-desc">点击上方按钮复制收款账户信息</div>
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">2</div>
            <div class="step-content">
              <div class="step-title">完成转账</div>
              <div class="step-desc">使用网银或手机银行转账 <strong class="highlight-amount">￥{{ formatAmount(payOrderInfo.amount) }}</strong></div>
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">3</div>
            <div class="step-content">
              <div class="step-title">等待确认</div>
              <div class="step-desc">转账完成后请耐心等待，系统会自动确认</div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- 加载遮罩 -->
    <div class="processing-mask" v-if="processing">
      <div class="processing-card">
        <div class="spinner"></div>
        <p class="processing-text">{{ processingText }}</p>
      </div>
    </div>

    <!-- 复制成功提示 -->
    <transition name="fade">
      <div class="toast" v-if="showToast">
        <div class="toast-content">
          <span class="toast-icon">✓</span>
          <span>{{ toastMessage }}</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { getPayOrderInfo, getPayOrderInfoState, updateCustomerInfo } from '@/api/api'
import config from '@/config'

export default {
  name: 'CashierFdrManual',
  data () {
    return {
      showPayerDialog: true, // 控制付款人姓名弹框显示
      payerName: '', // 付款人姓名
      payOrderInfo: {},
      loading: false,
      processing: false,
      processingText: '等待码商确认，请稍候…',
      pollTimer: null,
      showToast: false,
      toastMessage: '',
      countdown: 1800, // 30分钟倒计时（秒）
      countdownTimer: null,
      orderToken: '' // 用于标识当前订单
    }
  },
  mounted () {
    // 页面加载时聚焦到输入框
    this.$nextTick(() => {
      if (this.$refs.payerInput) {
        this.$refs.payerInput.focus()
      }
    })
    // 检查是否已经保存过付款人姓名
    const savedPayerName = sessionStorage.getItem('payerName')
    if (savedPayerName) {
      this.payerName = savedPayerName
      this.showPayerDialog = false
    }
    this.fetchPayOrderInfo()
    this.initCountdown()
    this.startOrderPolling()
  },
  beforeDestroy () {
    this.stopOrderPolling()
    this.stopCountdown()
    // 注意：不在这里清除缓存，因为用户可能只是刷新页面
  },
  computed: {
    formattedCountdown () {
      const minutes = Math.floor(this.countdown / 60)
      const seconds = this.countdown % 60
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }
  },
  methods: {
    fetchPayOrderInfo () {
      getPayOrderInfo().then(res => {
        this.payOrderInfo = res || {}
        // 使用订单号作为唯一标识
        this.orderToken = res.payOrderId || res.mchOrderNo || ''
      }).catch(err => {
        this.$router.push({ name: config.errorPageRouteName, params: { errInfo: err.msg } })
      })
    },
    initCountdown () {
      // 尝试从 sessionStorage 恢复倒计时
      const savedData = sessionStorage.getItem('payCountdown')
      if (savedData) {
        try {
          const { orderToken, expireTime } = JSON.parse(savedData)
          const now = Math.floor(Date.now() / 1000)
          const remaining = expireTime - now
          
          if (remaining > 0) {
            this.countdown = remaining
            this.orderToken = orderToken
          } else {
            // 已过期，清除缓存
            sessionStorage.removeItem('payCountdown')
          }
        } catch (e) {
          console.error('恢复倒计时失败', e)
        }
      }
      this.startCountdown()
    },
    saveCountdownState () {
      // 保存倒计时状态到 sessionStorage
      if (this.orderToken) {
        const expireTime = Math.floor(Date.now() / 1000) + this.countdown
        sessionStorage.setItem('payCountdown', JSON.stringify({
          orderToken: this.orderToken,
          expireTime: expireTime
        }))
      }
    },
    startOrderPolling () {
      this.stopOrderPolling()
      this.pollTimer = setInterval(() => {
        getPayOrderInfoState().then(info => {
          const state = info && (info.state !== undefined ? info.state : info.orderState)
          if (state === 2) {
            this.stopOrderPolling()
            this.gotoSuccess(info.returnUrl)
          } else if (state === 3) {
            this.stopOrderPolling()
            this.gotoFail(info.errMsg || '支付失败，请重试', info.returnUrl)
          }
        }).catch(() => {})
      }, 2000)
    },
    stopOrderPolling () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
      this.processing = false
    },
    startCountdown () {
      this.stopCountdown()
      this.saveCountdownState() // 首次保存
      
      this.countdownTimer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--
          // 每10秒保存一次状态
          if (this.countdown % 10 === 0) {
            this.saveCountdownState()
          }
        } else {
          this.stopCountdown()
          sessionStorage.removeItem('payCountdown')
          this.gotoFail('订单已超时，请重新下单', this.payOrderInfo.returnUrl)
        }
      }, 1000)
    },
    stopCountdown () {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },
    clearCountdownCache () {
      sessionStorage.removeItem('payCountdown')
    },
    gotoSuccess (returnUrl) {
      this.processing = false
      this.stopCountdown()
      this.clearCountdownCache()
      this.$router.push({
        name: 'PaySuccess',
        query: {
          mchOrderNo: (this.payOrderInfo && this.payOrderInfo.mchOrderNo) || '',
          amount: (this.payOrderInfo && this.payOrderInfo.amount) || '',
          mchName: (this.payOrderInfo && this.payOrderInfo.mchName) || '',
          returnUrl: returnUrl || ''
        }
      })
    },
    gotoFail (errMsg, returnUrl) {
      this.processing = false
      this.stopCountdown()
      this.clearCountdownCache()
      this.$router.push({
        name: 'PayFail',
        query: {
          mchOrderNo: (this.payOrderInfo && this.payOrderInfo.mchOrderNo) || '',
          amount: (this.payOrderInfo && this.payOrderInfo.amount) || '',
          errMsg: errMsg || '支付失败，请稍后再试',
          returnUrl: returnUrl || '',
          token: config.cacheToken || ''
        }
      })
    },
    formatAmount (amount) {
      if (!amount) return '0.00'
      return (amount / 100).toFixed(2)
    },
    formatCardNumber (cardNo) {
      if (!cardNo) return '-'
      // 银行卡号每4位加空格，便于阅读
      return cardNo.replace(/(\d{4})(?=\d)/g, '$1 ')
    },
    copyToClipboard (text, type) {
      if (!text) return
      
      // 创建临时文本区域
      const textarea = document.createElement('textarea')
      textarea.value = text.replace(/\s/g, '') // 移除空格
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      
      try {
        document.execCommand('copy')
        this.showToastMessage(`${type}已复制到剪贴板`)
      } catch (err) {
        this.showToastMessage('复制失败，请手动复制')
      } finally {
        document.body.removeChild(textarea)
      }
    },
    showToastMessage (message) {
      this.toastMessage = message
      this.showToast = true
      setTimeout(() => {
        this.showToast = false
      }, 2000)
    },
    submitPayerName () {
      if (!this.payerName || !this.payerName.trim()) {
        this.showToastMessage('请输入付款人姓名')
        return
      }
      
      // 显示加载状态
      this.processing = true
      this.processingText = '正在保存付款人信息...'
      
      // 调用后端接口保存付款人姓名到订单的 customerName 字段
      updateCustomerInfo(this.payerName.trim()).then(() => {
        // 保存成功
        // 保存到 sessionStorage，方便后续使用
        sessionStorage.setItem('payerName', this.payerName.trim())
        
        // 关闭弹框，显示收银台页面
        this.showPayerDialog = false
        
        this.showToastMessage('付款人姓名已确认')
      }).catch(err => {
        // 保存失败
        this.showToastMessage(err.msg || '保存失败，请重试')
      }).finally(() => {
        this.processing = false
        this.processingText = '等待码商确认，请稍候…'
      })
    }
  }
}
</script>

<style scoped>
.manual-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f0f6ff 0%, #fafbff 100%);
  padding-bottom: 40px;
}

/* 页面头部 */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 28px 20px 32px;
  text-align: center;
  color: #fff;
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 30% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
  pointer-events: none;
}

.header-icon-wrapper {
  margin-bottom: 10px;
}

.header-icon {
  font-size: 42px;
  display: inline-block;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 6px 0;
  letter-spacing: 0.5px;
}

.page-subtitle {
  font-size: 13px;
  opacity: 0.95;
  margin: 0 0 12px 0;
}

.header-countdown {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  margin-top: 8px;
}

.header-countdown .countdown-icon {
  font-size: 16px;
}

.header-countdown .countdown-text {
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;
}

/* 内容容器 */
.content-wrapper {
  max-width: 600px;
  margin: 0 auto;
  padding: 0 16px;
}

/* 警告提示区域 - 提前到顶部 */
.warning-section {
  margin: -20px 0 20px;
}

.warning-section:last-child {
  margin-bottom: 0;
}

/* 金额区域 */
.amount-section {
  margin-bottom: 20px;
}

.amount-card {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 16px;
  padding: 24px 20px;
  color: #fff;
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.3);
  position: relative;
  overflow: hidden;
}

.amount-card::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 60%);
  animation: shimmer 4s ease-in-out infinite;
}

@keyframes shimmer {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  50% { transform: translate(-15%, -15%) rotate(180deg); }
}

.amount-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.amount-icon {
  font-size: 24px;
}

.amount-label {
  font-size: 14px;
  opacity: 0.95;
  font-weight: 500;
}

.amount-value {
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 10px;
  letter-spacing: -0.5px;
}

.amount-order {
  font-size: 12px;
  opacity: 0.9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 区域样式 */
.account-section,
.steps-section {
  background: #fff;
  border-radius: 16px;
  padding: 24px 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.06);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f5ff;
}

.section-icon {
  font-size: 24px;
}

.section-title {
  font-size: 17px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.account-info {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.info-item:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.info-item.card-item {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 2px solid #3b82f6;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.15);
}

.info-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

.label-icon {
  font-size: 18px;
}

.info-value-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.info-value {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  flex: 1;
  word-break: break-all;
}

.info-value.card-number {
  font-size: 18px;
  font-weight: 700;
  color: #2563eb;
  letter-spacing: 2px;
  font-family: 'Courier New', monospace;
}

.copy-btn {
  background: #e0e7ff;
  border: none;
  border-radius: 8px;
  padding: 8px 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #4f46e5;
  white-space: nowrap;
}

.copy-btn:hover {
  background: #c7d2fe;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(79, 70, 229, 0.2);
}

.copy-btn:active {
  transform: translateY(0);
}

.copy-btn.primary {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: #fff;
}

.copy-btn.primary:hover {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.4);
}

.copy-icon {
  font-size: 14px;
}

.copy-text {
  font-size: 13px;
}

/* 步骤引导 */
.steps-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step-item {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.step-content {
  flex: 1;
  padding-top: 2px;
}

.step-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.step-desc {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
}

.highlight-amount {
  color: #10b981;
  font-weight: 700;
}



/* 警告提示 */
.warning-card {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 2px solid #ef4444;
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 4px 20px rgba(239, 68, 68, 0.2);
  animation: pulse-warning 2.5s ease-in-out infinite;
}

@keyframes pulse-warning {
  0%, 100% {
    box-shadow: 0 4px 20px rgba(239, 68, 68, 0.2);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 6px 24px rgba(239, 68, 68, 0.35);
    transform: scale(1.01);
  }
}

.warning-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.warning-icon {
  font-size: 26px;
  animation: shake 3s ease-in-out infinite;
}

@keyframes shake {
  0%, 100% { transform: rotate(0deg); }
  10%, 30% { transform: rotate(-6deg); }
  20%, 40% { transform: rotate(6deg); }
  50%, 90% { transform: rotate(0deg); }
}

.warning-title {
  font-size: 15px;
  font-weight: 700;
  color: #dc2626;
  margin: 0;
}

.warning-content {
  padding-left: 36px;
}

.warning-main {
  font-size: 14px;
  color: #b91c1c;
  font-weight: 700;
  margin: 0 0 4px 0;
  line-height: 1.5;
}

.warning-sub {
  font-size: 12px;
  color: #991b1b;
  margin: 0;
  line-height: 1.6;
}

/* 加载遮罩 */
.processing-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.processing-card {
  width: 260px;
  background: #fff;
  border-radius: 20px;
  padding: 32px 24px;
  text-align: center;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.spinner {
  width: 56px;
  height: 56px;
  margin: 0 auto 20px;
  border: 4px solid #f0f4ff;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.processing-text {
  font-size: 14px;
  color: #666;
  margin: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Toast 提示 */
.toast {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2000;
  pointer-events: none;
}

.toast-content {
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 12px 24px;
  border-radius: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.toast-icon {
  font-size: 18px;
  color: #4ade80;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* 响应式设计 */
@media (max-width: 480px) {
  .page-title {
    font-size: 20px;
  }
  
  .page-subtitle {
    font-size: 12px;
  }
  
  .header-countdown {
    font-size: 13px;
    padding: 5px 14px;
  }
  
  .amount-value {
    font-size: 32px;
  }
  
  .info-value.card-number {
    font-size: 16px;
    letter-spacing: 1px;
  }
  
  .copy-text {
    display: none;
  }
  
  .step-number {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }
  
  .warning-content {
    padding-left: 0;
  }
}

/* 付款人姓名弹框 */
.payer-dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.payer-dialog {
  width: 90%;
  max-width: 420px;
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
  overflow: hidden;
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.dialog-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 24px 20px;
  text-align: center;
}

.dialog-icon {
  font-size: 48px;
  display: block;
  margin-bottom: 12px;
  animation: bounce 2s ease-in-out infinite;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

.dialog-title {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  margin: 0;
}

.dialog-content {
  padding: 28px 24px;
}

.dialog-desc {
  font-size: 14px;
  color: #64748b;
  text-align: center;
  margin: 0 0 24px 0;
  line-height: 1.6;
}

.input-wrapper {
  margin-bottom: 16px;
}

.payer-input {
  width: 100%;
  padding: 14px 18px;
  font-size: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  outline: none;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.payer-input:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.payer-input::placeholder {
  color: #94a3b8;
}

.input-tip {
  font-size: 12px;
  color: #f59e0b;
  text-align: center;
  margin: 0;
  line-height: 1.5;
  background: #fef3c7;
  padding: 10px 14px;
  border-radius: 8px;
  border-left: 3px solid #f59e0b;
}

.dialog-footer {
  padding: 20px 24px;
  border-top: 1px solid #f1f5f9;
}

.submit-btn {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.submit-btn:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
  box-shadow: none;
}

.btn-text {
  letter-spacing: 2px;
}
</style>


